import { Col, Form, message, Modal, Row, Select, Spin, Switch, Radio, Space } from "antd";
import React, { useEffect, useState } from "react";
import { httpRequest } from "../../../helpers/api";
import { getErrorMessage } from "../../../helpers/errorHandler";
import { generateFormRules } from "../../../helpers/formRules";
import useAuth from "../../../hooks/useAuth";
import { BaseResponsePaginationProps } from "../../../types/config.type";
import { CreateExchangeBoardDto, EExchangeBoardType, ExchangeBoardProperties } from "../../../types/exchangeBoard.type";
import { ListingPropertyTypeProperties } from "../../../types/listingPropertyType.type";
import TextEditor from "../../../components/form/TextEditor";
import LandBuildingSizeForm from "./LandBuildingSizeForm";
import InputPrice from "../../../components/form/InputPrice";
import { usePropertyTypes } from "../../../hooks/usePropertyType";

interface FormValues {
	type: EExchangeBoardType;
	cityGroupId: string;
	landBuildingSize?: { landSize?: number; buildingSize?: number };
	budget: number;
	listingPropertyTypeId: string;
	content: string;
	isPublished: boolean;
}
interface CreateExchangeBoardModalProps {
	open?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
	isEdit?: boolean;
	exchangeBoard?: ExchangeBoardProperties;
}
export const CreateExchangeBoardModal: React.FC<CreateExchangeBoardModalProps> = ({
	open,
	onClose,
	onSuccess,
	isEdit,
	exchangeBoard,
}) => {
	const [form] = Form.useForm<FormValues>();
	const { user, officeId, employeeId } = useAuth();

	const [isListingPropertyTypeLoading, setIsListingPropertyTypeLoading] = useState<boolean>(false);
	// const [listingPropertyTypeList, setListingPropertyTypeList] = useState<ListingPropertyTypeProperties[]>([]);
	const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
	const [groupOfCityList, setGroupOfCityList] = useState<Array<{ cityGroupId: string; cityGroupName: string }>>([]);
	const [isGroupOfCityLoading, setIsGroupOfCityLoading] = useState<boolean>(false);

	const { propertyTypes: listingPropertyTypeList } = usePropertyTypes();

	useEffect(() => {
		const abort = new AbortController();

		// (async () => {
		// 	try {
		// 		setIsListingPropertyTypeLoading(true);
		// 		let { data } = await httpRequest.get<BaseResponsePaginationProps<ListingPropertyTypeProperties>>(
		// 			"listing-property-types?filterIsPublished=true"
		// 		);
		// 		const sortedType = data.payload.results.sort((a, b) =>
		// 			a.name.localeCompare(b.name, 'id')
		// 		);
		// 		setListingPropertyTypeList(sortedType);
		// 		setIsListingPropertyTypeLoading(false);
		// 	} catch (err) {
		// 		console.error(err);
		// 		message.error(getErrorMessage(err));
		// 	}
		// })();

		if (open) {
			(async () => {
				try {
					setIsGroupOfCityLoading(true);
					const { data } = await httpRequest.get<
						BaseResponsePaginationProps<{ cityGroupId: string; cityGroupName: string }>
					>("group-of-city?limit=1000");
					const sortedGroups = data.payload.results.sort((a, b) =>
						a.cityGroupName.localeCompare(b.cityGroupName, "id")
					);
					setGroupOfCityList(sortedGroups);
					setIsGroupOfCityLoading(false);
				} catch (err) {
					console.error(err);
					setGroupOfCityList([]);
					setIsGroupOfCityLoading(false);
				}
			})();

			return () => {
				abort.abort();
			};
		}
	}, [open]);

	useEffect(() => {
		if (isEdit && exchangeBoard) {
			form.setFieldsValue({
				type: exchangeBoard.type,
				cityGroupId: exchangeBoard.cityGroupId,
				landBuildingSize: {
					landSize: exchangeBoard.landSize,
					buildingSize: exchangeBoard.buildingSize,
				},
				budget: exchangeBoard.budget,
				listingPropertyTypeId: exchangeBoard.listingPropertyTypeId,
				content: exchangeBoard.title,
				isPublished: exchangeBoard.isPublished,
			});
		}

		// eslint-disable-next-line
	}, [isEdit, exchangeBoard]);

	const _handleSubmit = async (
		values: FormValues & { landBuildingSize?: { landSize?: number; buildingSize?: number } }
	) => {
		if (isEdit && !exchangeBoard?.exchangeBoardId) return message.error("Something went wrong, id not defined!");

		const picUserId = user.userId;
		const picEmployeeId = employeeId;
		const picOfficeId = officeId;

		setIsActionLoading(true);
		try {
			let dto: CreateExchangeBoardDto = {
				type: values.type,
				city: "",
				cityGroupId: values.cityGroupId,
				landSize: values.landBuildingSize?.landSize || 0,
				buildingSize: values.landBuildingSize?.buildingSize || 0,
				budget: values.budget,
				listingPropertyTypeId: values.listingPropertyTypeId,
				title: values.content,
				isPublished: values.isPublished,
				picUserId,
				picEmployeeId,
				picOfficeId,
			};
			if (isEdit) {
				await httpRequest.patch("exchange-boards/" + exchangeBoard?.exchangeBoardId, dto);
			} else {
				await httpRequest.post("exchange-boards", dto);
			}
			onSuccess?.();
			onClose?.();
		} catch (err) {
			console.error(err);
			message.error(getErrorMessage(err));
		}
		setIsActionLoading(false);
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={isEdit ? "Edit Posting" : "Create Posting"}
			destroyOnClose={true}
			okText={isEdit ? "Save" : "Create"}
			width={600}
			onOk={form.submit}
			okButtonProps={{ loading: isActionLoading }}
			cancelButtonProps={{ disabled: isActionLoading }}
		>
			<Form
				form={form}
				layout="vertical"
				preserve={false}
				onFinish={_handleSubmit}
				initialValues={{ isPublished: true }}
			>
				<Space
					direction="vertical"
					size={8}
					style={{ width: "100%" }}
				>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="type"
								label="Posting Type"
								rules={generateFormRules("Posting type", ["required"])}
							>
								<Radio.Group>
									<Radio value={EExchangeBoardType.WTB}>Want To Buy (WTB)</Radio>
									<Radio value={EExchangeBoardType.WTR}>Want To Rent (WTR)</Radio>
								</Radio.Group>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="isPublished"
								valuePropName="checked"
								label="Publish"
								rules={generateFormRules("Publish", ["required"])}
							>
								<Switch />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={12}>
							<Spin spinning={isGroupOfCityLoading}>
								<Form.Item
									name="cityGroupId"
									label="Area"
									rules={generateFormRules("Area", ["required"])}
								>
									<Select
										showSearch
										placeholder="Select Area"
										options={groupOfCityList.map((item) => ({
											value: item.cityGroupId,
											label: item.cityGroupName,
										}))}
										filterOption={(input, option) =>
											option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
										}
										virtual={true}
									/>
								</Form.Item>
							</Spin>
						</Col>
						<Col span={12}>
							<Form.Item
								name="listingPropertyTypeId"
								label="Property Type"
								rules={generateFormRules("Property type", ["required"])}
							>
								<Select
									virtual={true}
									placeholder="Select property type"
									options={listingPropertyTypeList.map((item) => ({
										value: item.listingPropertyTypeId,
										label: item.name,
									}))}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="landBuildingSize"
								label="Land Size / Building Size"
								rules={[
									...generateFormRules("Land size / Building size", ["required"]),
									{
										validator: (_, value) => {
											if (!value) {
												return Promise.reject(
													new Error("Land size and building size are required")
												);
											}
											if (
												value.landSize === undefined ||
												value.landSize === null ||
												value.landSize === ""
											) {
												return Promise.reject(new Error("Land size is required"));
											}
											if (
												value.buildingSize === undefined ||
												value.buildingSize === null ||
												value.buildingSize === ""
											) {
												return Promise.reject(new Error("Building size is required"));
											}
											if (value.landSize < 0) {
												return Promise.reject(new Error("Land size must be a positive number"));
											}
											if (value.buildingSize < 0) {
												return Promise.reject(
													new Error("Building size must be a positive number")
												);
											}
											return Promise.resolve();
										},
									},
								]}
							>
								<LandBuildingSizeForm
									landPlaceholder="Land size"
									buildingPlaceholder="Building Size"
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name="budget"
								label="Budget"
								rules={generateFormRules("Budget", ["required"])}
							>
								<InputPrice
									placeholder="Input budget"
									prefix="Rp"
									min={0}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Form.Item
						name="content"
						label="Content"
						rules={generateFormRules("Content", ["required", "min-20-characters"])}
					>
						<TextEditor
							showCounter={true}
							maxLength={5000}
							placeholder="Input Description"
						/>
					</Form.Item>
				</Space>
			</Form>
		</Modal>
	);
};
export default CreateExchangeBoardModal;
